import React, { useRef } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Grid, Button, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Accordion, AccordionSummary, AccordionDetails, Paper, Checkbox, ListItemText, CircularProgress, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { getRequest, patchRequest, deleteRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, PATCH_URL } from 'Includes/urls';
import { AddCircleOutlineOutlined, DownloadOutlined as DownloadOutlinedIcon, SwapHorizOutlined } from '@mui/icons-material';
import SummaryModal from 'Components/SummaryModal';
import InvoiceDetailsModal from 'Components/InvoiceDetailsModal';
import { loadBillingFilters, saveBillingFilters, clearBillingCompanyFilter } from 'Includes/billingFilterStorage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const uniqueCompanyOptions = (rows, toOption) => {
  const byId = new Map();
  const seenNames = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const companyId = row?.company != null && row.company !== '' ? String(row.company) : '';
    const companyName = String(row?.company_name || '').trim();
    if (!companyId || !companyName) return;
    const nameKey = companyName.toLowerCase();
    if (byId.has(companyId) || seenNames.has(nameKey)) return;
    byId.set(companyId, toOption(row, companyName));
    seenNames.add(nameKey);
  });
  return Array.from(byId.values());
};

const filterCompanyOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return options;
  return options.filter((option) => String(option?.company_name || '').toLowerCase().includes(query));
};

function CompanyIssueInvoiceList(props) {
  const navigate = useNavigate();
  const storedFilters = loadBillingFilters();
  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [academicYearList, setAcademicYearList] = React.useState([])
  const [companies, setCompanies] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [summaryOpen, setSummaryOpen] = React.useState(false)
  const [summaryData, setSummaryData] = React.useState(null)
  const [statusWiseSummary, setStatusWiseSummary] = React.useState(null)
  const [selectedCompany, setSelectedCompany] = React.useState(null)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortModel, setSortModel] = React.useState([
    { field: 'issue_date', sort: 'desc' }
  ])
  const [invoiceDialogOpen, setInvoiceDialogOpen] = React.useState(false)
  const [invoiceRow, setInvoiceRow] = React.useState(null)
  const [invoiceItems, setInvoiceItems] = React.useState([])
  const [searchKey, setSearchKey] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState(['DRAFT', 'ISSUED', 'INVOICED', 'PARTIAL_PAID', 'PAID'])
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false)
  const [statusRow, setStatusRow] = React.useState(null)
  const [statusValue, setStatusValue] = React.useState('DRAFT')
  const [statusSaving, setStatusSaving] = React.useState(false)
  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false)
  const [downloadRow, setDownloadRow] = React.useState(null)
  const [downloadMode, setDownloadMode] = React.useState('all')
  const [downloadSaving, setDownloadSaving] = React.useState(false)
  const [downloadShowGst, setDownloadShowGst] = React.useState(true)
  const [downloadShowDiscount, setDownloadShowDiscount] = React.useState(true)
  const [downloadShowBillingSummary, setDownloadShowBillingSummary] = React.useState(false)
  const [downloadBillingSummary, setDownloadBillingSummary] = React.useState('')
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)
  const [createDialogCompany, setCreateDialogCompany] = React.useState(null)

  const ALL_INSTITUTES_VALUE = '__ALL__'
  const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = React.useState(false)
  const [bulkDownloadCompanies, setBulkDownloadCompanies] = React.useState([ALL_INSTITUTES_VALUE])
  const [bulkDownloadShowGst, setBulkDownloadShowGst] = React.useState(true)
  const [bulkDownloadShowDiscount, setBulkDownloadShowDiscount] = React.useState(true)
  const [bulkDownloadShowBillingSummary, setBulkDownloadShowBillingSummary] = React.useState(false)
  const [bulkDownloadBillingSummary, setBulkDownloadBillingSummary] = React.useState('')
  const [bulkDownloadSaving, setBulkDownloadSaving] = React.useState(false)

  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = React.useState(false)
  const [bulkStatusCompanies, setBulkStatusCompanies] = React.useState([])
  const [bulkStatusFromFilter, setBulkStatusFromFilter] = React.useState(['DRAFT', 'ISSUED'])
  const [bulkStatusValue, setBulkStatusValue] = React.useState('ISSUED')
  const [bulkStatusSaving, setBulkStatusSaving] = React.useState(false)

  const isSuperuser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      return Boolean(u?.is_superuser)
    } catch {
      return false
    }
  }, [])
  const onLoadFetch = useRef(false);

  const STATUS_CHOICES = ['DRAFT', 'ISSUED', 'INVOICED', 'PARTIAL_PAID', 'PAID']

  const formatStatusLabel = (code) => {
    const map = {
      DRAFT: 'Draft',
      ISSUED: 'Issued',
      INVOICED: 'Invoiced',
      PARTIAL_PAID: 'Partial paid',
      PAID: 'Paid',
    }
    return map[code] || code || '-'
  }

  /** Next steps: Draft → Issued → Invoiced (Invoiced creates the GST row for Payments). */
  const getManualStatusChoices = (currentStatus) => {
    const s = (currentStatus || 'DRAFT').toUpperCase()
    if (s === 'DRAFT') return ['DRAFT', 'ISSUED']
    if (s === 'ISSUED') return ['ISSUED', 'INVOICED', 'DRAFT']
    return [s]
  }

  const companySelectOptions = React.useMemo(
    () => [{ company: ALL_INSTITUTES_VALUE, company_name: 'All institutes' }, ...companies],
    [companies],
  )

  const resolveBulkCompanyIds = (selectedCompanies) => {
    const values = Array.isArray(selectedCompanies) ? selectedCompanies : []
    if (values.includes(ALL_INSTITUTES_VALUE) || values.length === 0) {
      return companies.map((c) => c.company).filter(Boolean)
    }
    return values.filter((id) => id !== ALL_INSTITUTES_VALUE)
  }

  const handleBulkDownloadDialogOpen = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning')
      return
    }
    setBulkDownloadCompanies([ALL_INSTITUTES_VALUE])
    setBulkDownloadShowGst(true)
    setBulkDownloadShowDiscount(true)
    setBulkDownloadShowBillingSummary(false)
    setBulkDownloadBillingSummary('')
    setBulkDownloadDialogOpen(true)
  }

  const handleBulkDownloadDialogClose = () => {
    setBulkDownloadDialogOpen(false)
    setBulkDownloadSaving(false)
  }

  const handleBulkDownloadSubmit = () => {
    if (!selectedAcademicYear) return
    const companyIds = resolveBulkCompanyIds(bulkDownloadCompanies)
    if (!companyIds.length) {
      Swal.fire('No institutes', 'No billing configs found for this academic year.', 'warning')
      return
    }

    setBulkDownloadSaving(true)
    const url = GET_URL.issued_invoices.api
    const reqProps = { ...props, responseType: 'blob' }
    const requestParams = {
      year: selectedAcademicYear,
      bulk_download_pdf: 1,
      company_ids: companyIds.join(','),
      show_gst: bulkDownloadShowGst ? 1 : 0,
      show_discount: bulkDownloadShowDiscount ? 1 : 0,
      show_billing_summary: bulkDownloadShowBillingSummary ? 1 : 0,
      ...(Array.isArray(statusFilter) && statusFilter.length ? { status: statusFilter.join(',') } : {}),
      ...(searchKey ? { key: searchKey } : {}),
      ...(bulkDownloadShowBillingSummary && bulkDownloadBillingSummary.trim()
        ? { billing_summary: bulkDownloadBillingSummary.trim() }
        : {}),
    }

    getRequest(url, requestParams, reqProps)
      .then((response) => {
        setBulkDownloadSaving(false)
        if (response && response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/zip' })
          const fileURL = window.URL.createObjectURL(blob)
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
          const match = typeof contentDisposition === 'string'
            ? contentDisposition.match(/filename="?([^"]+)"?/i)
            : null
          const filename = match?.[1] || 'bulk_proforma_invoices.zip'
          const link = document.createElement('a')
          link.href = fileURL
          link.download = filename
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(fileURL)
          document.body.removeChild(link)
          handleBulkDownloadDialogClose()
        }
      })
      .catch((error) => {
        setBulkDownloadSaving(false)
        Swal.fire({
          icon: 'error',
          title: 'Download failed',
          text: error?.response?.data?.Reason || error?.response?.data?.detail || 'Unable to download bulk invoices.',
        })
      })
  }

  const handleBulkStatusDialogOpen = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning')
      return
    }
    setBulkStatusCompanies([ALL_INSTITUTES_VALUE])
    setBulkStatusFromFilter(['DRAFT', 'ISSUED'])
    setBulkStatusValue('ISSUED')
    setBulkStatusDialogOpen(true)
  }

  const handleBulkStatusDialogClose = () => {
    setBulkStatusDialogOpen(false)
    setBulkStatusSaving(false)
  }

  const handleBulkStatusSubmit = () => {
    if (!selectedAcademicYear) return
    const companyIds = resolveBulkCompanyIds(bulkStatusCompanies)
    if (!companyIds.length) {
      Swal.fire('No institutes', 'No billing configs found for this academic year.', 'warning')
      return
    }
    if (!bulkStatusFromFilter.length) {
      Swal.fire('Select status', 'Choose at least one current status to update.', 'warning')
      return
    }

    setBulkStatusSaving(true)
    const url = `${GET_URL.issued_invoices.api}bulk-change-status/`
    const payload = {
      year: selectedAcademicYear,
      company_ids: companyIds.join(','),
      status: bulkStatusFromFilter.join(','),
      new_status: bulkStatusValue,
    }

    postRequest(url, payload, props)
      .then((response) => {
        setBulkStatusSaving(false)
        if (response && response.status === 200) {
          const result = response.data?.data || {}
          const updated = result.updated || 0
          const skipped = result.skipped || 0
          Swal.fire({
            icon: updated > 0 ? 'success' : 'info',
            title: updated > 0 ? 'Status updated' : 'No changes',
            text: response.data?.Reason || `Updated ${updated}, skipped ${skipped}.`,
          })
          if (selectedAcademicYear) {
            getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, page, pageSize, sortModel)
          }
          handleBulkStatusDialogClose()
        }
      })
      .catch((error) => {
        setBulkStatusSaving(false)
        Swal.fire({
          icon: 'error',
          title: 'Update failed',
          text: error?.response?.data?.Reason || error?.response?.data?.detail || 'Unable to change invoice status.',
        })
      })
  }

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusWiseSummary = () => {
    // Use backend summary if available, otherwise return empty summary
    if (statusWiseSummary) {
      return statusWiseSummary;
    }
    
    // Fallback: return empty summary structure
    const summary = {};
    STATUS_CHOICES.forEach(status => {
      summary[status] = {
        total_without_tax: 0,
        tax_amount: 0,
        total_amount: 0,
        count: 0
      };
    });
    return summary;
  };

  const enrichSummaryPayload = (payload) => {
    if (!payload || !Array.isArray(payload.pricing_services)) return Promise.resolve(payload);
    const updatedServices = payload.pricing_services.map(svc => {
      const serviceAmount = parseFloat(svc.service_amount || 0) || 0;
      const gstPercent = parseFloat(svc.service_gst_percentage || 0) || 0;
      const itemCount = parseFloat(svc.item_count || 1) || 1;
      let baseAmount = serviceAmount * (itemCount || 1);
      const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
      const totalAmount = baseAmount + gstAmount;
      return { ...svc, service_base: baseAmount, service_gst: gstAmount, service_total: totalAmount };
    });
    return { ...payload, pricing_services: updatedServices };
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'items', headerName: 'items', width: 90, hide: true },
    { field: 'all_items_invoiced', headerName: 'all_items_invoiced', width: 90, hide: true },

    {
      field: 'company_name',
      headerName: 'Company name',
      width: 150,
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice Num',
      width: 150,
    },
    {
      field: 'issue_date',
      headerName: 'Issue Date',
      width: 150,
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 150,
    },
    {
      field: 'total_amount',
      headerName: 'Total Amount',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      valueGetter: (params) => formatStatusLabel(params.row?.status),
    },
    {
      field: 'created_by_name',
      headerName: 'Created By',
      width: 160,
      valueGetter: (params) => params.row?.created_by_name || '-',
    },
    {
      field: 'modified_by_name',
      headerName: 'Modified By',
      width: 160,
      valueGetter: (params) => params.row?.modified_by_name || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => {
        const rowStatus = (params.row?.status || '').toUpperCase();
        const actions = [];
        if (['DRAFT', 'ISSUED'].includes(rowStatus)) {
          actions.push(
            <GridActionsCellItem key="change-status" label="Change status" onClick={() => handleOpenStatusDialog(params)} showInMenu />,
          )
        }
        if (!['PARTIAL_PAID', 'PAID'].includes(rowStatus)) {
          actions.push(
            <GridActionsCellItem key="edit-row" label="Edit invoice" onClick={() => goToEditIssueInvoice(params)} showInMenu />,
          );
        }
        actions.push(
          <GridActionsCellItem key="download-invoice" label="Download Invoice" onClick={() => handleDownloadInvoice(params)} showInMenu />,
        );
        actions.push(
          <GridActionsCellItem key="view-details" label="View Details" onClick={() => handleViewDetails(params)} showInMenu />,
        );
        if (['DRAFT', 'ISSUED'].includes(rowStatus)) {
          actions.push(
            <GridActionsCellItem key="delete-row" label="Delete invoice" onClick={() => handleDeleteIssueInvoice(params)} showInMenu />,
          );
        }
        return actions;
      }
    }
  ];

  const handleOpenStatusDialog = (params) => {
    const row = params?.row || null
    if (!row) return
    const currentStatus = (row.status || 'DRAFT').toUpperCase()
    if (!['DRAFT', 'ISSUED'].includes(currentStatus)) return
    setStatusRow(row)
    const choices = getManualStatusChoices(currentStatus)
    setStatusValue(choices.includes(currentStatus) ? currentStatus : choices[0])
    setStatusDialogOpen(true)
  }

  const goToEditIssueInvoice = (params) => {
    const row = params?.row || null
    if (!row?.id || row.billing_config == null) return
    navigate(`/billing/issue-invoice/?config_id=${row.billing_config}&issue_invoice_id=${row.id}`)
  }

  const handleViewDetails = (params) => {
    const row = params?.row || null;
    if (!row || !row.id) return;
    setInvoiceRow(row);
    const items = Array.isArray(row.items) ? row.items : [];
    setInvoiceItems(items);
    setInvoiceDialogOpen(true);
  }

  const handleDownloadInvoice = (params) => {
    const row = params?.row || null;
    if (!row?.id) return;
    setDownloadRow(row)
    setDownloadMode('all')
    setDownloadSaving(false)
    setDownloadShowGst(true)
    setDownloadShowDiscount(true)
    setDownloadShowBillingSummary(false)
    setDownloadBillingSummary('')
    setDownloadDialogOpen(true)
  }

  const closeDownloadDialog = () => {
    setDownloadDialogOpen(false)
    setDownloadRow(null)
    setDownloadMode('all')
    setDownloadSaving(false)
  }

  const handleDownloadDialogClose = () => {
    if (downloadSaving) return
    closeDownloadDialog()
  }

  const handleDownloadInvoiceSubmit = () => {
    if (!downloadRow?.id) return
    setDownloadSaving(true)
    const url = `${GET_URL.issued_invoices.api}${downloadRow.id}/`
    const reqProps = { ...props, responseType: 'blob' }
    const requestParams = {
      generate_pdf: 1,
      show_gst: downloadShowGst ? 1 : 0,
      show_discount: downloadShowDiscount ? 1 : 0,
      show_billing_summary: downloadShowBillingSummary ? 1 : 0,
      ...(downloadMode === 'separate' ? { separate_by_service: 1 } : {}),
      ...(downloadShowBillingSummary && downloadBillingSummary.trim()
        ? { billing_summary: downloadBillingSummary.trim() }
        : {}),
    }

    getRequest(url, requestParams, reqProps)
      .then((response) => {
        setDownloadSaving(false)
        if (response && response.status === 200) {
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
          const match = typeof contentDisposition === 'string'
            ? contentDisposition.match(/filename="?([^"]+)"?/i)
            : null
          const contentType = (
            response?.headers?.['content-type'] || response?.headers?.['Content-Type'] || ''
          ).toLowerCase()
          const filenameFallback = downloadMode === 'separate'
            ? `invoice_${downloadRow.invoice_number || downloadRow.id}_services.zip`
            : `invoice_${downloadRow.invoice_number || downloadRow.id}.pdf`
          const filename = match?.[1] || filenameFallback
          const isZip = contentType.includes('zip') || String(filename).toLowerCase().endsWith('.zip')
          const blob = new Blob([response.data], { type: isZip ? 'application/zip' : 'application/pdf' })
          const fileURL = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = fileURL
          link.download = filename
          document.body.appendChild(link)
          link.click()

          window.URL.revokeObjectURL(fileURL)
          document.body.removeChild(link)
        }
        closeDownloadDialog()
      })
      .catch((error) => {
        setDownloadSaving(false)
        // eslint-disable-next-line no-console
        console.error('Error downloading invoice PDF:', error)
        Swal.fire({
          icon: 'error',
          title: 'Download failed',
          text: error?.response?.data?.Reason || error?.response?.data?.detail || 'Unable to download invoice.',
        })
      })
  }

  const handleInvoiceDialogClose = () => {
    setInvoiceDialogOpen(false);
    setInvoiceRow(null);
    setInvoiceItems([]);
  }

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false)
    setStatusRow(null)
    setStatusValue('DRAFT')
    setStatusSaving(false)
  }

  const handleStatusSubmit = () => {
    if (!statusRow?.id) return
    setStatusSaving(true)
    const url = `${PATCH_URL.issued_invoices.api}${statusRow.id}/`
    const payload = { status: statusValue }
    patchRequest(url, payload, props)
      .then((response) => {
        setStatusSaving(false)
        if (response && response.status === 200 && selectedAcademicYear) {
          getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, page, pageSize, sortModel)
        }
        handleStatusDialogClose()
      })
      .catch(() => setStatusSaving(false))
  }

  const handleDeleteIssueInvoice = (params) => {
    const row = params?.row || null;
    if (!row?.id) return;
    const rowStatus = String(row.status || '').toUpperCase();
    if (!['DRAFT', 'ISSUED'].includes(rowStatus)) return;

    const url = `${GET_URL.issued_invoices.api}${row.id}/`;
    deleteRequest(url, {}, props)
      .then((response) => {
        if (response && response.status === 200 && selectedAcademicYear) {
          getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, page, pageSize, sortModel);
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            text: 'Issued invoice deleted successfully.',
            timer: 1500,
            showConfirmButton: false,
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Delete failed',
          text: error?.response?.data?.Reason || error?.response?.data?.detail || 'Unable to delete issued invoice.',
        });
      });
  }

  const getCompanies = async (year) => {
    const url = GET_URL.company_billing_config.api
    const response = await getRequest(url, { year }, props)
      if (response && response.status === 200) {
        const data = response.data.data || response.data || []
        // Unique by company id and by display name — duplicate institute records otherwise repeat in the dropdown.
        const uniqueCompanies = uniqueCompanyOptions(data, (row, companyName) => ({
          ...row,
          company_name: companyName,
        }))
        setCompanies(uniqueCompanies)
        const sf = loadBillingFilters()
        let companyId = sf.companyId
        if (!companyId && sf.companyConfigId) {
          const raw = data.find((c) => String(c.id) === String(sf.companyConfigId))
          companyId = raw?.company
        }
        const match = companyId
          ? uniqueCompanies.find((c) => String(c.company) === String(companyId))
          : null
        if (match) {
          setSelectedCompany(match)
          setPage(0)
          getIssuedInvoices(year, match.company, 0, pageSize, sortModel)
        } else {
          setSelectedCompany(null)
          getIssuedInvoices(year, null, 0, pageSize, sortModel)
        }
      }
  }

  React.useEffect(() => {
    getAcademicYearList()
  }, [])

  const getAcademicYearList = () => {
    const url = GET_URL.academicyear.api;
    const params = {};
    getRequest(url, params, props).then((response) => {
      if (response && response.status === 200) {
        setAcademicYearList(response.data.data);
        if (selectedAcademicYear) {
          let academicYrFound = false;
          for (const yr of response.data.data) {
            if (yr.id === selectedAcademicYear) {
              academicYrFound = true;
              getCompanies(yr.id);
              break;
            }
          }
          if (!academicYrFound) {
            setSelectedAcademicYear('')
          }
        }
      }
    });
  };

  const handleAcademicYearChange = (event) => {
    const yearId = event.target.value;
    setSelectedAcademicYear(yearId);
    clearBillingCompanyFilter()
    setSelectedCompany(null)
    setRows([])
    setStatusWiseSummary(null)
    if (yearId) {
      saveBillingFilters({ academicYearId: yearId })
      getCompanies(yearId);
      // getIssuedInvoices(yearId, null, 0, pageSize)
    }
  };

  const getIssuedInvoices = async (academicYearId, compId, p = page, ps = pageSize, sort = sortModel) => {
    const url = GET_URL.issued_invoices.api
    const params = {
      year: academicYearId,
      company: compId || undefined,
      limit: ps,
      pageno: (p + 1),
      ...(searchKey ? { key: searchKey } : {}),
      ...(Array.isArray(statusFilter) && statusFilter.length ? { status: statusFilter.join(',') } : {})
    }
    // Add sort parameters
    if (sort && sort.length > 0) {
      const sortItem = sort[0]
      params.ordering = sortItem.sort === 'desc' ? `-${sortItem.field}` : sortItem.field
    }
    const response = await getRequest(url, params, props)
    onLoadFetch.current = true;
      if (response && response?.status === 200) {
        const data = response.data?.data || response.data || []
        for (const invData of data) {
          invData.all_items_invoiced = true
          for (const [itemOrderId, count] of Object.entries(invData.issue_invoice_item_count)) {
            const paid_inv_count = invData?.paid_invoice_item_count[itemOrderId] || 0
            if (paid_inv_count < count) {
              invData.all_items_invoiced = false
              break
            }
          }
        }
        const count = response.data?.count || (Array.isArray(data) ? data.length : 0)
        setRows(() => data)
        setRowCount(() => count)
        
        // Set status-wise summary from backend (without pagination)
        if (response.data?.summary) {
          setStatusWiseSummary(response.data.summary)
        } else {
          setStatusWiseSummary(null)
        }
      }
  }

  const handleCompanyChange = (_e, value) => {
    setSelectedCompany(value)
    if (selectedAcademicYear) {
      saveBillingFilters({
        academicYearId: selectedAcademicYear,
        companyId: value?.company,
        companyConfigId: value?.id,
        companyName: value?.company_name,
      })
      setPage(0)
      getIssuedInvoices(selectedAcademicYear, value?.company, 0, pageSize, sortModel)
    }
  }

  const continueCreateIssueInvoice = (companyConfig) => {
    if (!companyConfig?.id) {
      Swal.fire('Select company', 'Please select a company first', 'warning');
      return;
    }
    saveBillingFilters({
      academicYearId: selectedAcademicYear,
      companyId: companyConfig?.company,
      companyConfigId: companyConfig?.id,
      companyName: companyConfig?.company_name,
    });
    setCreateDialogOpen(false);
    setCreateDialogCompany(null);
    navigate(`/billing/issue-invoice/?config_id=${companyConfig.id}`);
  };

  React.useEffect(() => {
    if (selectedAcademicYear && onLoadFetch.current) {
      setPage(0)
      getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, 0, pageSize, sortModel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, statusFilter, sortModel])

  const handlePageChange = (newPage) => {
    setPage(newPage)
    if (selectedAcademicYear) {
      getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, newPage, pageSize, sortModel)
    }
  }

  const handlePageSizeChange = (newPageSize) => {
    const resetPage = 0
    setPageSize(newPageSize)
    setPage(resetPage)
    if (selectedAcademicYear) {
      getIssuedInvoices(selectedAcademicYear, selectedCompany?.company || null, resetPage, newPageSize, sortModel)
    }
  }

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel)
    setPage(0) // Reset to first page when sort changes
    if (selectedAcademicYear) {
      getIssuedInvoices(selectedAcademicYear, selectedCompany?.company, 0, pageSize, newSortModel)
    } else if (selectedAcademicYear) {
      getIssuedInvoices(selectedAcademicYear, null, 0, pageSize, newSortModel)
    }
  }

  const goToCreateIssueInvoice = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    if (selectedCompany?.id) {
      continueCreateIssueInvoice(selectedCompany);
      return;
    }
    setCreateDialogCompany(null);
    setCreateDialogOpen(true);
  }

  const goToBulkCreateIssueInvoice = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    navigate(`/billing/issue-invoice/bulk-create?year=${selectedAcademicYear}`);
  }

  const handleDownloadExcel = () => {
    if (!selectedAcademicYear) return
    const params = {
      year: selectedAcademicYear,
      ...(selectedCompany?.company ? { company: selectedCompany?.company } : {}),
      ...(searchKey ? { key: searchKey } : {}),
      ...(statusFilter?.length ? { status: statusFilter.join(',') } : {}),
    }
    const url = GET_URL.issued_invoices.api
    const reqProps = { ...props, responseType: 'blob' }
    getRequest(url, { ...params, export: 1 }, reqProps).then((response) => {
      if (response && response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
        const fileURL = window.URL.createObjectURL(blob)
        const contentDisposition =
          response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
        const match = typeof contentDisposition === 'string'
          ? contentDisposition.match(/filename="?([^"]+)"?/i)
          : null
        const filename = match?.[1] || `issued_invoices_year_${selectedAcademicYear}.csv`
        const link = document.createElement('a')
        link.href = fileURL
        link.download = filename
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(fileURL)
        document.body.removeChild(link)
      }
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Issued invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track draft through paid for each company and academic year.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
          {isSuperuser && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadExcel}
              disabled={!selectedAcademicYear}
            >
              Download Excel
            </Button>
          )}
          {selectedAcademicYear && (
            <Button
              variant="outlined"
              color="primary"
              sx={{ mr: 1 }}
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleBulkDownloadDialogOpen}
            >
              Bulk download PDF
            </Button>
          )}
          {selectedAcademicYear && (
            <Button
              variant="outlined"
              color="primary"
              sx={{ mr: 1 }}
              startIcon={<SwapHorizOutlined />}
              onClick={handleBulkStatusDialogOpen}
            >
              Bulk change status
            </Button>
          )}
          {selectedAcademicYear && (
            <Button
              variant="outlined"
              color="primary"
              sx={{ mr: 1 }}
              startIcon={<AddCircleOutlineOutlined />}
              onClick={goToBulkCreateIssueInvoice}
            >
              Bulk issue invoice
            </Button>
          )}
          {selectedAcademicYear && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={goToCreateIssueInvoice}
            >
              Issue invoice
            </Button>
          )}
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
  
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={handleAcademicYearChange}
                label="Academic Year"
              >
                {academicYearList.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
  
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.company_name || ''}
              getOptionKey={(option) => String(option?.company)}
              isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
              filterOptions={filterCompanyOptions}
              value={selectedCompany}
              onChange={handleCompanyChange}
              renderInput={(params) => (
                <TextField {...params} label="Select Company" size="small" />
              )}
              fullWidth
            />
          </Grid>
  
          <Grid item xs={12} md={3}>
            <TextField
              label="Search Invoice #"
              size="small"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              fullWidth
            />
          </Grid>
  
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  renderValue={(selected) =>
                    Array.isArray(selected) && selected.length
                      ? selected.map((s) => formatStatusLabel(s)).join(', ')
                      : 'All statuses'
                  }
                >
                  {STATUS_CHOICES.map(s => (
                    <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {statusFilter.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => setStatusFilter([])}
                  aria-label="Clear status filter"
                  sx={{ mt: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
  
        </Grid>
      </Paper>
  
      {/* SUMMARY */}
      {selectedAcademicYear && isSuperuser ? (() => {
        const statusSummary = getStatusWiseSummary();
        const hasAnyData = STATUS_CHOICES.some(status => statusSummary[status]?.count > 0);
        if (!hasAnyData) return null;
  
        return (
          <Accordion sx={{ mt: 2, borderRadius: 3 }} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} color="primary">
                Overall summary
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                {STATUS_CHOICES.map(status => {
                  const summary = statusSummary[status];
                  if (summary.count === 0) return null;

                  return (
                    <Grid item xs={12} sm={6} md={3} key={status}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography fontWeight={600} color="primary">
                          {formatStatusLabel(status)} ({summary.count})
                        </Typography>
  
                        <Typography variant="caption">
                          Excl: {formatAmount(summary.total_without_tax)}
                        </Typography>
  
                        <Typography variant="caption" display="block">
                          Tax: {formatAmount(summary.tax_amount)}
                        </Typography>
  
                        <Typography variant="subtitle2" fontWeight="bold">
                          {formatAmount(summary.total_amount)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })() : null}
  
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
          autoHeight
          rowHeight={42}
          sx={{ border: 0 }}
        />
      </Paper>
  
      {/* MODALS */}
      <InvoiceDetailsModal
        open={invoiceDialogOpen}
        onClose={handleInvoiceDialogClose}
        invoiceRow={invoiceRow}
        invoiceItems={invoiceItems}
        props={props}
      />
  
      <SummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        onSubmit={() => setSummaryOpen(false)}
        data={summaryData}
      />
  
      <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Change status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move Draft → Issued → Invoiced in order. Invoiced creates the bill used under Payments. Partial paid and paid follow from payments there.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              label="Status"
            >
              {statusRow && getManualStatusChoices(statusRow.status || 'DRAFT').map(s => (
                <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusDialogClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleStatusSubmit} disabled={statusSaving}>
            {statusSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={downloadDialogOpen} onClose={handleDownloadDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Download invoice PDF</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Services</InputLabel>
            <Select
              value={downloadMode}
              label="Services"
              onChange={(e) => setDownloadMode(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="separate">Separate</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All keeps every service on one PDF. Separate downloads one invoice PDF per service.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Show GST</InputLabel>
            <Select
              value={downloadShowGst ? 'yes' : 'no'}
              label="Show GST"
              onChange={(e) => setDownloadShowGst(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Show Discount</InputLabel>
            <Select
              value={downloadShowDiscount ? 'yes' : 'no'}
              label="Show Discount"
              onChange={(e) => setDownloadShowDiscount(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Add Billing Summary</InputLabel>
            <Select
              value={downloadShowBillingSummary ? 'yes' : 'no'}
              label="Add Billing Summary"
              onChange={(e) => setDownloadShowBillingSummary(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          {downloadShowBillingSummary ? (
            <TextField
              fullWidth
              multiline
              minRows={4}
              sx={{ mt: 2 }}
              label="Billing Summary"
              value={downloadBillingSummary}
              onChange={(e) => setDownloadBillingSummary(e.target.value)}
              placeholder="Type billing summary text to include in PDF..."
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadDialogClose} disabled={downloadSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleDownloadInvoiceSubmit} disabled={downloadSaving}>
            {downloadSaving ? 'Preparing…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDownloadDialogOpen} onClose={handleBulkDownloadDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Bulk download proforma invoices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download PDFs for selected institutes. Defaults to all institutes for the chosen academic year.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Institute</InputLabel>
            <Select
              multiple
              value={bulkDownloadCompanies}
              label="Institute"
              onChange={(e) => {
                const next = e.target.value
                if (next.includes(ALL_INSTITUTES_VALUE)) {
                  setBulkDownloadCompanies([ALL_INSTITUTES_VALUE])
                  return
                }
                setBulkDownloadCompanies(next.filter((v) => v !== ALL_INSTITUTES_VALUE))
              }}
              renderValue={(selected) => {
                if (!Array.isArray(selected) || selected.includes(ALL_INSTITUTES_VALUE)) {
                  return 'All institutes'
                }
                return selected
                  .map((id) => companies.find((c) => String(c.company) === String(id))?.company_name || id)
                  .join(', ')
              }}
            >
              <MenuItem value={ALL_INSTITUTES_VALUE}>
                <Checkbox checked={bulkDownloadCompanies.includes(ALL_INSTITUTES_VALUE)} />
                <ListItemText primary="All institutes" />
              </MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.company} value={c.company}>
                  <Checkbox checked={bulkDownloadCompanies.includes(c.company)} />
                  <ListItemText primary={c.company_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Show GST</InputLabel>
            <Select
              value={bulkDownloadShowGst ? 'yes' : 'no'}
              label="Show GST"
              onChange={(e) => setBulkDownloadShowGst(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Show Discount</InputLabel>
            <Select
              value={bulkDownloadShowDiscount ? 'yes' : 'no'}
              label="Show Discount"
              onChange={(e) => setBulkDownloadShowDiscount(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Add Billing Summary</InputLabel>
            <Select
              value={bulkDownloadShowBillingSummary ? 'yes' : 'no'}
              label="Add Billing Summary"
              onChange={(e) => setBulkDownloadShowBillingSummary(e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          {bulkDownloadShowBillingSummary ? (
            <TextField
              fullWidth
              multiline
              minRows={4}
              sx={{ mt: 2 }}
              label="Billing Summary"
              value={bulkDownloadBillingSummary}
              onChange={(e) => setBulkDownloadBillingSummary(e.target.value)}
              placeholder="Type billing summary text to include in each PDF..."
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDownloadDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkDownloadSubmit} disabled={bulkDownloadSaving}>
            {bulkDownloadSaving ? <CircularProgress size={22} /> : 'Download ZIP'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkStatusDialogOpen} onClose={handleBulkStatusDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Bulk change invoice status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update status for invoices matching the selected institutes and current status. Invoices that cannot move to the new status are skipped.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Institute</InputLabel>
            <Select
              multiple
              value={bulkStatusCompanies}
              label="Institute"
              onChange={(e) => {
                const next = e.target.value
                if (next.includes(ALL_INSTITUTES_VALUE)) {
                  setBulkStatusCompanies([ALL_INSTITUTES_VALUE])
                  return
                }
                setBulkStatusCompanies(next.filter((v) => v !== ALL_INSTITUTES_VALUE))
              }}
              renderValue={(selected) => {
                if (!Array.isArray(selected) || !selected.length) {
                  return 'Select institutes'
                }
                if (selected.includes(ALL_INSTITUTES_VALUE)) {
                  return 'All institutes'
                }
                return selected
                  .map((id) => companies.find((c) => String(c.company) === String(id))?.company_name || id)
                  .join(', ')
              }}
            >
              <MenuItem value={ALL_INSTITUTES_VALUE}>
                <Checkbox checked={bulkStatusCompanies.includes(ALL_INSTITUTES_VALUE)} />
                <ListItemText primary="All institutes" />
              </MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.company} value={c.company}>
                  <Checkbox checked={bulkStatusCompanies.includes(c.company)} />
                  <ListItemText primary={c.company_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Current status</InputLabel>
            <Select
              multiple
              value={bulkStatusFromFilter}
              label="Current status"
              onChange={(e) => setBulkStatusFromFilter(e.target.value)}
              renderValue={(selected) =>
                Array.isArray(selected) ? selected.map((s) => formatStatusLabel(s)).join(', ') : ''
              }
            >
              {['DRAFT', 'ISSUED'].map((s) => (
                <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>New status</InputLabel>
            <Select
              value={bulkStatusValue}
              label="New status"
              onChange={(e) => setBulkStatusValue(e.target.value)}
            >
              {['DRAFT', 'ISSUED', 'INVOICED'].map((s) => (
                <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkStatusDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkStatusSubmit} disabled={bulkStatusSaving}>
            {bulkStatusSaving ? <CircularProgress size={22} /> : 'Update status'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Select company</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a company before creating an issue invoice.
          </Typography>
          <Autocomplete
            options={companies}
            getOptionLabel={(option) => option.company_name || ''}
            getOptionKey={(option) => String(option?.company)}
            isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
            filterOptions={filterCompanyOptions}
            value={createDialogCompany}
            onChange={(_e, value) => setCreateDialogCompany(value || null)}
            renderInput={(params) => (
              <TextField {...params} label="Company" size="small" />
            )}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => continueCreateIssueInvoice(createDialogCompany)}
            disabled={!createDialogCompany?.id}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
  
    </Box>
  );

}

export default CompanyIssueInvoiceList;
